import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import { BaseComponentProps } from '@/pages/benchmark/types/commom';
import CardBox from '@/pages/dashboard/components/CardBox';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import { getName } from '@home/utils/common';
import { IBaseTooltipProps } from '@/pages/dashboard/components/EchartsTooltip/type';
import classNames from 'classnames';
import { xAxisHighLight } from '@/pages/dashboard/components/Chart/_config/plug';
import { handleAxisFormat} from '@/pages/dashboard/utils/utils';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import { getEchartTooltipString } from '@/pages/dashboard/components/EchartsTooltip';
import CommonBubble from '@home/components/CommonBubble';
import useStudioExplain from '@home/hooks/useStudioExplain';
import SvgIcon from '@home/components/SvgIcon';
import { xAxisMonthAndQuarterFormat, xAxisMonthAndSundayFormat } from '@home/components/Chart/_config/plug';
import moment from 'moment';
import { KPI_PERIOD_NAME_MAP, LABEL_PERIOD_NAME_MAP } from '../../_constant';
import { dynamicString } from '@home/utils/dynamicString';
import './index.scss';
import { Checkbox } from 'tea-component';
import { getKpiTrendEventData } from '@home/services/studio';
import { useParams } from 'umi';
import EventWindows from '@op/pages/Overview/KeyMetrics/MentionsSentiment/MetricsChart/Charts/BarAndLine/EventWindows';
import { ChartRef } from '@/pages/dashboard/components/Chart/_config/interface';
import { debounce } from 'lodash-es';
import { formatEventData } from '../../../utils';


export interface IRevenueTrendProps extends BaseComponentProps {
  tooltipConfig?: IBaseTooltipProps['options'];
  titleEn?: string;
  titleCn?: string;
  explainEn?: string;
  explainCn?: string;
  componentMetrics?: {
    metricKey: string;
    nameEn: string;
    nameCn: string;
    tooltipNameEn?: string;
    tooltipNameCn?: string;
    tooltipNameWidthEn?: number;
    tooltipNameWidthCn?: number;
    lineType: string;
  }[];
  [key: string]: any;
}

const RevenueTrend = (props: IRevenueTrendProps) => {
  const {
    style,
    className,
    trendConfig,
    trendCommonConfig,
    dataKey = 'revenueTrendData',
    data,
    filterDate,
    infoTextObj
  } = props || {};
  // 趋势图配置
  const { componentMetrics, lineChartHeight = 330, yAxisFormatter = "bmk", allImg, upToWeekLabelFormatter = 'percent2Decimal' } = trendCommonConfig || [];
  const { isEN } = useLocal();
  const { changeDataItem, period, showEventGameCodes } = data || {};
  const { selectedSegment, selectedGame } = changeDataItem || {};
  const { value: metricsValue } = selectedSegment || {};
  const { studioId } = useParams() || {} as any;
  const periodKey = period as keyof typeof KPI_PERIOD_NAME_MAP;
  // 季度名称
  const quarterName = getName(isEN, KPI_PERIOD_NAME_MAP[periodKey]?.nameEn, KPI_PERIOD_NAME_MAP[periodKey]?.nameCn) || '';
  // 接口数据结构
  const { errorMsg = "", loading, data: trendData } = data[dataKey] || {};
  // 趋势图数据
  const { trend_list: trendList } = trendData || [];
  const filteredTrendList = period === 'year' ? trendList : trendList.filter((item: { period: string; }) => item.period === period);
  // 数据period映射
  const dataPeriod = period === 'year' ? 'year' : 'quarter';
  const { titleEn, titleCn, explainEn, explainCn, rainbowKey, dataFormatRule } = trendConfig?.[metricsValue] || {};
  const chartRef = useRef<ChartRef>(null);
  const [infoShow, setInfoShow] = useState(false);
  const [isHover, setIsHover] = useState(false);
  const [showEvent, setShowEvent] = useState(true);
  const [eventData, setEventData] = useState<any[]>([]);
  const [eventLoading, setEventLoading] = useState(true);
  const isHaveEvent = useMemo(() => showEventGameCodes?.includes(selectedGame?.key), [showEventGameCodes, selectedGame?.key]);
  const [chartInstance, setChartInstance] = useState<any>(null);

  // info图标位置
  const [position, setPosition] = useState<{x?: number; y?: number;}>({});
  const [eventResizeKey, setEventResizeKey] = useState(0);

  const { tipEn, tipCn } = useStudioExplain(rainbowKey, explainEn, explainCn) || {};
  // const { date_type: dataType, isSingle, start_time: startTime, end_time: endTime, payType } = filter || {};
  useEffect(() => {
    const chart = chartRef.current?.instance?.();
    if (chart) {
      setChartInstance(chart);
    }
  }, [loading, isEN, period]);

  useEffect(() => {
    const handleResize = debounce(() => {
      if (chartInstance) {
        chartInstance.resize(); // 重要：调整图表大小
      }
      setEventResizeKey(prev => prev + 1); // 触发 EventWindows 重新渲染
    }, 200);
  
    window.addEventListener('resize', handleResize);
    
    return () => {
      handleResize.cancel();
      window.removeEventListener('resize', handleResize);
    };
  }, [chartInstance]);

  const { xData, seriesData } = useMemo(() => {
    const seriesData =
      componentMetrics?.map(
        (item: {
          metricKey: string;
          nameCn: string;
          nameEn: string;
          lineType: string;
          color: string;
          labelEnName: string;
          labelCnName: string;
          endLabelConfig?: any;
        }) => {
          const {
            metricKey,
            nameCn,
            nameEn,
            lineType,
            color,
            labelEnName,
            labelCnName,
            endLabelConfig,
          } = item || {};
          const name = getName(isEN, nameEn, nameCn) || '';
          const labelName = getName(isEN, labelEnName, labelCnName) || '';
  
          return {
            type: 'line',
            name,
            lineStyle: {
              type: lineType,
            },
            data:
              filteredTrendList?.map((item: any) => ({
                value: item?.metrics?.[metricsValue]?.[dataPeriod]?.[metricKey],
                date: item?.date,
              })) || [],
            color,
            // label: {
            //   formatter: (params: any) => {
            //     const value = dataFormat({ value: params.value, formatRule: kpiConfig?.formatRule });
            //     return `${params?.name} ${value}`;
            //   },
            //   position: labelPosition,
            //   color: '#EF5858',
            // },
            endLabel: {
              formatter: (params: any) => {
                const year = moment(params?.name, 'YYYYMMDD').format('YYYY');
                const labePeriod = dataPeriod === 'year' ? year : quarterName;
                const value = dataFormat({
                  value: params.value,
                  formatRule: endLabelConfig?.formatRule,
                });
                return `${labePeriod} ${labelName} ${value}`;
              },
              color: endLabelConfig?.color,
              show: !!endLabelConfig,
              offset: endLabelConfig?.offset,
            },
            labelName,
          };
        },
      ) || [];
    // 添加一个计算位置的数据进去
    seriesData.push({
      name: 'TopTipsEvent',
      type: "line",
      stack: 'Total',
      data: [],
    });
  
    return {
      xData: filteredTrendList?.map((item: any) => moment(item.date).format('YYYY-MM-DD')) || [],
      seriesData,
    };
  }, [componentMetrics, filteredTrendList, isEN, metricsValue, dataPeriod, quarterName]);
  
  // 浮窗
  const getTooltipText = (paramsVal: any) => {
    const params = paramsVal?.filter((item: any) => {
      const obj = componentMetrics?.filter((items: { nameEn: string; nameCn: string; }) => items?.nameEn === item?.seriesName || items?.nameCn === item?.seriesName)?.[0];
      return obj?.showTooltip;
    });
    if (params.length === 0) {
      return null;
    }
    const dataIndex = params[0]?.dataIndex;
    let endDate = filteredTrendList[dataIndex]?.date;
    const isSunday = moment(endDate)?.day() === 0;
    // 非周日不展示浮窗
    if (!isSunday) {
      return null;
    }
    endDate = moment(endDate)?.format("YYYY-MM-DD");

    let startDate: moment.Moment | string = moment(endDate).clone().subtract(6, 'days');
    if (dataPeriod === 'year') {
      // 年维度：如果是跨年了，则获取本年的第一天
      if (startDate.year() !== moment(endDate).year()) {
        startDate = moment(`${moment(endDate).year()}-01-01`);
      }
    } else {
      // 季度维度：获取季度的第一天
      const startOfQuarter = moment(endDate).clone().startOf('quarter');
      // 如果是跨季度了，则获取该季度的第一天
      if (startDate.isBefore(startOfQuarter)) {
        startDate = startOfQuarter;
      }
    }
    startDate = startDate?.format("YYYY-MM-DD");

    // 获取当前hover的原始数据
    // const currentOrgData = data?.[metricKey]?.[dataIndex] || {};
    // const { date, value } = currentOrgData;

    const tooltipData = params?.map((item: any) => {
      const { seriesName, color, componentIndex, value } = item;

      const {
        tooltipNameEn,
        tooltipNameCn,
      } = componentMetrics[componentIndex] || {};

      // 有自定义的浮窗名称则优先取
      const name =
        tooltipNameEn && tooltipNameCn
          ? getName(isEN, tooltipNameEn, tooltipNameCn)
          : seriesName;

      return {
        name,
        value,
        color
      };
    });

    return getEchartTooltipString({
      table: {
        data: tooltipData,
        columns: [
          {
            key: 'name',
            header: `${startDate} ~ ${endDate}`,
            showIcon: true,
          },
          {
            key: 'value',
            header: '',
            dataFormatRule,
            align: 'right',
          },
        ],
      },
    });
  };

  const getLastSunday = (dateString: string) => {
    const date = moment(dateString);
    const dayOfWeek = date.day();
    // 判断是否是周日

    if (dayOfWeek === 0) {
      return {
        isSunday: true,
        date: date.format("YYYYMMDD"),
      };
    }
    // 如果不是周日，则获取上周日日期
    const lastSunday = date.subtract(dayOfWeek, 'days');
    return {
      isSunday: false,
      date: lastSunday.format("YYYYMMDD")
    };
  };

  const renderList = useMemo(
    () => [
      {
        type: 'lineChart',
        component: (
          <>
            <LineChart
              ref={chartRef}
              key={isEN?.toString()}
              hasDataZoom={true}
              height={lineChartHeight}
              xAxisData={xData}
              series={seriesData}
              formatterFunc={getTooltipText}
              notMerge={false}
              setOtherConfigFunc={(opt: any) => {
              // X轴格式化
                opt = period === "year" ? xAxisMonthAndQuarterFormat(opt) : xAxisMonthAndSundayFormat(opt);
                // Y轴格式化
                opt.yAxis.axisLabel.formatter = (value: any) => {
                  if (value >= 1000000) {
                    const hundredThousandDigit = Math.floor((value % 1000000) / 100000); // 获取十万位
                    const formatRule = hundredThousandDigit === 0 ? yAxisFormatter : "bmk0"; // 判断是否为整数百万
                    return dataFormat({ value, formatRule });
                  }
                  return dataFormat({ value, formatRule: yAxisFormatter }); // 默认使用 bmk
                };
                // X轴高亮
                opt = xAxisHighLight(opt, {
                  formatter: (params: any) =>
                    handleAxisFormat(params.value, 'Day'),
                });
                opt.yAxis.splitNumber = 5;
                opt.yAxis.scale = true;
                opt.yAxis.splitLine.show = !(isHaveEvent && showEvent);
                opt.yAxis.alignTicks = !(isHaveEvent && showEvent);

                // 找到seriesData中的最大值
                // const maxValues = seriesData.map(series => Math.max(...series.data.map(item => item.value || 0)));
                // const maxValue = Math.max(...maxValues);

                // 设置y轴最大值为最大值的一点三倍
                // opt.yAxis.max = maxValue * 1.3;

                // 隐藏kpi图例
                opt.legend = {
                  show: true,
                  ...(opt?.legend || {}),
                  data: seriesData
                    .filter((item: { name: string; }) => {
                      const obj = componentMetrics?.filter((items: { nameEn: string; nameCn: string; }) => items?.nameEn === item?.name || items?.nameCn === item?.name)?.[0];
                      return obj?.showLegend;
                    })
                    .map(({ name }: { name: string; }) => name),
                };
                const kpiData = opt.series?.filter((item: { name: string; }) => item.name === 'KPI')?.[0]?.data;
                const THRESHOLD = 0.05;
                // eslint-disable-next-line arrow-body-style
                opt.series = opt.series?.map((seriesItem: { data: any[]; labelName: string; }, seriesIndex: number) => {
                  const data = opt.series[seriesIndex]?.data;
                  // 最后一个value有值的Item
                  const lastValidItem = [...data].reverse().find(item => item?.value && item.value !== '0');
                  // 获取上个周日点
                  const valueObj = getLastSunday(lastValidItem?.date);
                  const dataIndex = data?.findIndex((item: { date: string; }) => item?.date === valueObj?.date);
                  const kpiVal = kpiData?.[dataIndex]?.value;
                  const currentVal = data?.[dataIndex]?.value;
                  // 计算 label 位置，默认 currentVal 在上方
                  let positionVal = 'top';

                  if (currentVal && kpiVal) {
                    const diff = Math.abs(currentVal - kpiVal);
                    if (diff < THRESHOLD * kpiVal) {
                    // 差距过小时，根据大小调整位置
                      positionVal = currentVal >= kpiVal ? 'top' : 'bottom';
                    } else {
                    // 差距较大时，两个始终在上
                      positionVal = 'top';
                    }
                  }
                  const total = data?.length || 0;
                  const seriesData = seriesItem?.data?.map((item, index) => {

                    // 展示数据标签的折线
                    const seriesShowBol = seriesIndex === 0;
                    // 是否展示数值
                    const valueShowBol = seriesShowBol && index === dataIndex;
                    // 是否是单独的圆点
                    const isAloneSymbol = (seriesItem?.data[index - 1] === '' || seriesItem?.data[index - 1] === undefined) &&
                    (seriesItem?.data[index + 1] === '' || seriesItem?.data[index + 1] === undefined);
                    // 计算 offset
                    let offset: [number, number] = [0, 0]; // 默认不偏移
                    if (index < 10) offset = [125, 0]; // 前5个
                    if (index >= total - 10) offset = [-125, 0]; // 最后5个
                    return {
                      value: item?.value,
                      label: {
                        position: positionVal,
                        show: valueShowBol,
                        color: '#5086F3',
                        offset,
                      },
                      itemStyle: {
                        borderColor: '#fff',
                        borderWidth: 1,
                        borderType: 'solid',

                        opacity: valueShowBol || isAloneSymbol ? 1 : 0
                      },
                      symbolSize: 10,
                      symbol: 'circle'
                    };
                  });

                  if (!opt.grid) {
                    opt.grid = {};
                  }
                  opt.grid = {
                    ...opt.grid,
                    right: 50,
                    top: isHaveEvent && showEvent ? 80 : 20,
                  };

                  return {
                    ...seriesItem,
                    data: seriesData,
                    showAllSymbol: true,
                    label: {
                      normal: {
                        formatter: (params: any) => {
                          const date = params?.name;
                          const isShow = (date === valueObj?.date);
                          const periodLabel = getName(isEN, LABEL_PERIOD_NAME_MAP[dataPeriod]?.nameEn, LABEL_PERIOD_NAME_MAP[dataPeriod]?.nameCn);
                          return isShow ? `${periodLabel} ${seriesItem?.labelName} ${dataFormat({
                            value: kpiVal ? params.value / kpiVal : '',
                            formatRule: upToWeekLabelFormatter,
                          })}` : '';
                        },
                      }
                    },
                  };
                });         
                return opt;
              }}
            />
          </>
        ),
      },
    ],
    [isEN, seriesData, xData,isHaveEvent, xData, showEvent],
  );

  // info提示文案
  const infoText = getName(isEN, infoTextObj?.textEn, infoTextObj?.textCn);

  const setLineChartEndLabelPosition = () => {
    const chart = chartRef.current?.instance?.();
    if (chart) {
      // 获取kpi线
      const seriesModel = chart.getModel()?.getSeriesByIndex(1);
      const currentData = seriesModel?.getData();
      // 获取最后一个数据点的索引
      const dataIndex = currentData?.count() - 1;
      // 获取最后一个数据点的坐标、偏移量
      const endLabelModel = currentData?.getItemModel(dataIndex)?.get('endLabel');
      const labelData = currentData?.getItemGraphicEl(dataIndex);
      const { x, y } = labelData || {};

      // 坐标无效时（KPI线无数据点），不设置位置，隐藏 icon
      if (x === undefined || y === undefined || Number.isNaN(x) || Number.isNaN(y)) {
        setInfoShow(false);
        return;
      }

      // -5是线和文字空行高度
      const endLabelOffset = endLabelModel?.offset?.[1] - 5 || -17;
      // x 20+10是容器padding 10是图标偏移量，y 30 + 20 标题+padding, endLabelOffset是文字的偏移量, 2图标大小偏移量
      let left = x + 20 + 10 + 10;
      const top = y + 30 + 20 + endLabelOffset - 2;
      if (period !== 'year') {
        left -= 10;
      }
      setPosition({ x: left, y: top });
    }
  };

  const { isNodeData, isKpiNoData } = useMemo(() => {
    let isKpiNoData = false;
    const isNodeData = seriesData?.every((item: any, index: number) => {
      const dataLength = item?.data?.filter((v: any) => v.value !== "")?.length;
      // KPI下标为1
      if (index === 1) {
        isKpiNoData = dataLength === 0;
      }
      return dataLength === 0;
    });
    return { isNodeData, isKpiNoData };
  }, [seriesData]);

  useEffect(() => {
    if (!infoText) return;
    const chart = chartRef.current?.instance?.();
    chart?.off('rendered', setLineChartEndLabelPosition);
    setInfoShow(false);
    if (loading) return;
    if (isNodeData || isKpiNoData) return;
    chart?.on('rendered', setLineChartEndLabelPosition);
    setInfoShow(true);
  }, [loading, isEN, period, isNodeData, isKpiNoData, infoText]);

  const getEventData = (params: { game_codes: string[]; }) => {
    const { game_codes: gameCodes } = params || {};
    // setIsError(false);
    setEventLoading(true);
    const paramsData = {
      studio_code: studioId,
      game_codes: gameCodes,
      logical_flag: "sales_kpi_v2",
      period: "weekly",
      start_time: filterDate,
      end_time: filterDate
    };
    getKpiTrendEventData(paramsData).then((res) => {
      setEventLoading(false);
      setEventData(formatEventData(res) || []);
    }).catch(_error => {
      setEventLoading(false);
      // setIsError(true);

    });
  };
  useEffect(() => {
    if (showEventGameCodes?.includes(selectedGame?.key) && isHaveEvent) {
      getEventData({
        game_codes: [selectedGame?.key],
      });
    }
  }, [filterDate, selectedGame?.key]);

  useEffect(() => {
    // 当 period 变化时，重置并触发重新渲染
    setEventResizeKey(prev => prev + 1);
  }, [period]);

  const filteredEventData = useMemo(() => {
    // 如果没有数据，直接返回空对象
    if (isNodeData || !eventData || !xData || xData.length === 0) {
      return {};
    }
  
    // 创建 xData 的 Set 以便快速查找
    const xDataSet = new Set(xData);
  
    // 过滤 eventData，只保留在 xData 中存在的日期
    const filtered: Record<string, any[]> = {};
    
    Object.keys(eventData).forEach(date => {
      if (xDataSet.has(date)) {
        filtered[date] = eventData[date];
      }
    });
  
    return filtered;
  }, [eventData, xData, isNodeData]);

  return (
    <div className='dbh-revenue-trend-container'>
      <CardBox
        style={style}
        className={classNames(className, 'dbh-revenue-trend')}
        height={320}
        titleCom={
          <CommonBubble content={getName(isEN, tipEn, tipCn)}>
            <div className="dbh-revenue-trend-title">
              <div
                className={classNames({
                  'dbh-revenue-trend-title-img-not-all': selectedGame?.value !== 'All',
                  'dbh-revenue-trend-title-img-all': selectedGame?.value === 'All',
                })}
              >
                {selectedGame?.img && <img className="dbh-revenue-trend-title-img" src={selectedGame?.img} />}
                {selectedGame?.value === 'All' && <img src={allImg || ''} />}
              </div>
              {
                dynamicString(getName(isEN, titleEn, titleCn), {
                  gameName: selectedGame?.value === 'All' ? '' : getName(isEN, selectedGame?.textEn, selectedGame?.textCn),
                  quarter: quarterName,
                  year: moment(filterDate)?.year()
                })
              }
            </div>
            {isHaveEvent && !isNodeData && (
              <div className='dbh-revenue-trend-event-container'>
                <Checkbox
                  value={showEvent}
                  onChange={(value) => {
                    setShowEvent(!!value);
                  }}
                >
                  <div className='dbh-revenue-trend-event-title'>{getName(isEN, 'Show Events', '显示事件')}</div>
                </Checkbox>
              </div>
            )}
          </CommonBubble>
        }
        renderList={renderList}
        isLoading={loading}
        isError={!!errorMsg}
        isNodata={isNodeData}
      /> 
      {infoText && (
        <CommonBubble content={<div dangerouslySetInnerHTML={{ __html: infoText?.replaceAll('\\n', '<br/>') }}></div>}>
          <div
            className='dbh-revenue-trend-info-icon'
            style={{ left: position?.x, top: position?.y, display: infoShow ? 'block' : 'none' }}
            onMouseEnter={() => setIsHover(true)}
            onMouseLeave={() => setIsHover(false)}
          >
            <SvgIcon type={isHover ? 'alert-circle-action' : 'alert-circle-m'} />
          </div>
        </CommonBubble>
      )}
      {isHaveEvent && showEvent && !eventLoading && !loading && (
        <div className="dbh-revenue-trend-event-wrapper">
          <EventWindows
            key={eventResizeKey}
            myChart={chartInstance}
            eventList={filteredEventData}
            format={'YYYY-MM-DD'}
            maxTimeValue={xData?.[xData.length - 1]}
            showEventAxis={true}
            dateType={'daily'}
            xAxisData={xData}
          />
        </div>
      )}
    </div>
  );
};
export default RevenueTrend;